import {
  ExhibitionArtistData,
  ExhibitionArtistInputData,
  ExhibitionData,
  SimpleExhibitionData,
} from '../../../shared/dto/EntityRepository';
import {
  CreateExhibitionReq,
  ExhibitionArtistReq,
  UpdateExhibitionReq,
} from '../../../shared/dto/ReqDtoRepository';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const text = (value?: string | null): string => (value ?? '').trim();

const optionalText = (value?: string | null): string | undefined => {
  const normalized = text(value);
  return normalized || undefined;
};

const normalizeArtist = (artist: ExhibitionArtistData): ExhibitionArtistData => ({
  artistId: artist.artistId != null && Number(artist.artistId) > 0
    ? Number(artist.artistId)
    : null,
  name: artist.name ?? '',
  nameEn: artist.nameEn ?? '',
  instagramHandle: artist.instagramHandle ?? '',
  architraveHandle: artist.architraveHandle ?? '',
});

const toArtistReq = (artist: ExhibitionArtistData): ExhibitionArtistReq => {
  const input: ExhibitionArtistInputData = {
    name: text(artist.name),
    nameEn: optionalText(artist.nameEn),
    instagramHandle: optionalText(artist.instagramHandle),
    architraveHandle: optionalText(artist.architraveHandle),
  };

  if (artist.artistId != null && artist.artistId > 0) {
    return {
      ...input,
      resolutionType: 'LINK_EXISTING',
      artistId: artist.artistId,
    };
  }
  return input;
};

export const emptyExhibition = (): ExhibitionData => ({
  id: 0,
  venueCode: '',
  title: '',
  titleEn: '',
  startDate: '',
  endDate: '',
  officialUrl: '',
  sourceUrl: '',
  artists: [],
});

export const normalizeExhibitionDetail = (
  exhibition: ExhibitionData,
): ExhibitionData => ({
  ...exhibition,
  id: Number(exhibition.id) || 0,
  venueCode: exhibition.venueCode ?? '',
  title: exhibition.title ?? '',
  titleEn: exhibition.titleEn ?? '',
  startDate: exhibition.startDate ?? '',
  endDate: exhibition.endDate ?? '',
  officialUrl: exhibition.officialUrl ?? '',
  sourceUrl: exhibition.sourceUrl ?? '',
  artists: (exhibition.artists ?? []).map(normalizeArtist),
});

export const toExhibitionDetailPreview = (
  exhibition: SimpleExhibitionData,
): ExhibitionData => normalizeExhibitionDetail({
  ...exhibition,
  sourceUrl: '',
});

export const toCreateExhibitionReq = (
  exhibition: ExhibitionData,
): CreateExhibitionReq => ({
  venueCode: text(exhibition.venueCode),
  title: text(exhibition.title),
  titleEn: optionalText(exhibition.titleEn),
  startDate: text(exhibition.startDate),
  endDate: text(exhibition.endDate),
  officialUrl: optionalText(exhibition.officialUrl),
  sourceUrl: text(exhibition.sourceUrl),
  artists: exhibition.artists.map(toArtistReq),
});

export const toUpdateExhibitionReq = (
  exhibition: ExhibitionData,
): UpdateExhibitionReq => toCreateExhibitionReq(exhibition);

export const isSameExhibitionDraft = (
  left: ExhibitionData,
  right: ExhibitionData,
): boolean => JSON.stringify(toUpdateExhibitionReq(left))
  === JSON.stringify(toUpdateExhibitionReq(right));

export const isExhibitionDraftMissingRequired = (
  exhibition: ExhibitionData,
): boolean => !text(exhibition.venueCode)
  || !text(exhibition.title)
  || !text(exhibition.startDate)
  || !text(exhibition.endDate)
  || !text(exhibition.sourceUrl);

export const isValidExhibitionDate = (value: string): boolean => {
  const normalized = text(value);
  if (!ISO_DATE_PATTERN.test(normalized)) return false;

  const [year, month, day] = normalized.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
};

export const isExhibitionDateRangeValid = (
  exhibition: ExhibitionData,
): boolean => isValidExhibitionDate(exhibition.startDate)
  && isValidExhibitionDate(exhibition.endDate)
  && text(exhibition.startDate) <= text(exhibition.endDate);

export type ExhibitionDraftValidationError =
  | 'MISSING_REQUIRED'
  | 'INVALID_DATE';

export const getExhibitionDraftValidationError = (
  exhibition: ExhibitionData,
): ExhibitionDraftValidationError | null => {
  if (isExhibitionDraftMissingRequired(exhibition)) return 'MISSING_REQUIRED';
  if (!isExhibitionDateRangeValid(exhibition)) return 'INVALID_DATE';
  return null;
};
